import { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
    Box,
    Theme,
    Typography,
    Select,
    MenuItem,
    FormControl,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as LogoIcon } from '../../logo.svg';
import { getUser } from '../../api/usersApi/usersApi';
import useAuth from '../../hooks/useAuth';
import { UserRole } from '../../types';
import useSnackBar from '../../hooks/useSnackBar';
import theme from '../../theme';


const useStyles = makeStyles()((theme: Theme) => ({
    headerWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        backgroundColor: theme.palette.primary.light,
        borderBottom: `1px solid ${theme.palette.secondary.dark}`,
        maxHeight: '60px',
    },
    logoInsideBox: {
        display: 'flex',
        alignItems: 'center',
        '& svg': {
            height: '30px',
            width: 'auto',
        },
        '& img': {
            objectFit: 'contain',
            display: 'block',
        },
    },
    userBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '5px 15px',
        margin: '5px 15px',
        border: `1px solid ${theme.palette.secondary.dark}`,
        // borderRadius: '6px',
        userSelect: 'none',
        // '&:hover': {
        //     backgroundColor: 'rgba(255,255,255,0.05)',
        // },
    },
    userName: {
        fontSize: '12px',
        fontWeight: 600,
        color: theme.palette.common.white,
        fontFamily: 'Poppins',
        lineHeight: 1.2,
    },
    userRole: {
        fontSize: '10px',
        color: theme.palette.secondary.main,
        fontFamily: 'Poppins',
        lineHeight: 1.2,
    },
    arrowIcon: {
        color: theme.palette.common.white,
        fontSize: '18px',
    },
    selectRoot: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        margin: '5px 30px',
        background: 'rgba(255, 255, 255, 0.03)',
        borderRadius: '12px',
        border: `1px solid ${theme.palette.secondary.dark}`,
        userSelect: 'none',
        
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.dark,
            borderRadius: '12px',
        },

        '& .MuiOutlinedInput-input': {
            color: theme.palette.common.white,
            padding: '12px 16px',
            borderRadius: '12px',

            '&.Mui-disabled': {
                color: theme.palette.text.disabled,
                WebkitTextFillColor: theme.palette.text.disabled,
            },
        },

        '& .MuiOutlinedInput-root': {
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderWidth: '2px',
                borderColor: theme.palette.info.main,
            },

            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: '2px',
                borderColor: theme.palette.info.main,
            },

            '&.Mui-disabled': {
                color: theme.palette.text.disabled,
            },
        },

        '& .MuiSelect-select': {
            padding: '8px',
            paddingRight: '0 !important',
            backgroundColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            '&:focus': {
                backgroundColor: 'transparent',
            },
        },

        '& .MuiSelect-icon': {
            display: 'none',
        },
    },
    selectPaper: {
        backgroundColor: `${theme.palette.secondary.dark} !important`,
        color: theme.palette.common.white,
        // border: `1px solid ${theme.palette.secondary.dark}`,
        // borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',

        '& .MuiList-root': {
            backgroundColor: 'transparent',
            padding: '4px',
        },
    },
    selectMenuItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: theme.palette.common.white,
        fontSize: '14px',
        fontWeight: 500,
        fontFamily: 'Inter',
        padding: '12px 16px',
        '& svg': {
            fontSize: '18px',
            color: theme.palette.secondary.main,
        },
    },
    selectMenuItemDanger: {
        color: '#f44336',
        '& svg': {
            color: '#f44336',
        },
    },
}));

const Header = () => {
    const { classes } = useStyles();
    const navigate = useNavigate();
    const { setAlert } = useSnackBar();

    const [selectValue, setSelectValue] = useState<string>('');
    const [userName, setUserName] = useState<string>('');
    const [userRole, setUserRole] = useState<string>('');
    const [logoImage, setLogoImage] = useState<string | null>(null);
    const [logoDimensions, setLogoDimensions] = useState<{ width: number; height: number } | null>(null);

    const MAX_LOGO_HEIGHT = 40;
    const MAX_LOGO_WIDTH = MAX_LOGO_HEIGHT * 3; // 3:1 ratio (120 x 40)

    const handleLogoLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const { naturalWidth, naturalHeight } = e.currentTarget;
        const scale = Math.min(
            MAX_LOGO_HEIGHT / naturalHeight,
            MAX_LOGO_WIDTH / naturalWidth,
            1
        );
        setLogoDimensions({
            width: Math.round(naturalWidth * scale),
            height: Math.round(naturalHeight * scale),
        });
    };

    const { onLogOut } = useAuth();

    useEffect(() => {
        const userId = localStorage.getItem('userId');

        if (userId) {
            getUser(userId)
                .then((res) => {
                    if (res.data) {
                        setUserName(`${res.data.firstName} ${res.data.lastName}`);
                        setUserRole((res.data.roles?.[0] as UserRole)?.roleName || '');
                        setLogoImage(res.data.firm.logoImageUrl || null);
                    }
                })
                .catch(() => {
                    setAlert((prev) => ({
                        ...prev,
                        message: 'Cannot load account data.',
                        type: 'error',
                    }));
                })
        }
    }, []);

    const handleSelectChange = (value: string) => {
        setSelectValue('');
        if (value === 'logout') {
            onLogOut && onLogOut();
        } else {
            navigate(value);
        }
    };

    return (
        <Box className={classes.headerWrapper}>
            <Box sx={{ margin: '5px 20px' }}>
                <Link to="/">
                    <LogoIcon />
                </Link>
            </Box>
            <FormControl size="small" className={classes.selectRoot}>
                <Select
                    value={selectValue}
                    onChange={(e) => handleSelectChange(e.target.value)}
                    displayEmpty
                    renderValue={() => (
                        <>
                            <Box className={classes.logoInsideBox}>
                                {logoImage ? (
                                    <img
                                        src={logoImage}
                                        alt="Logo"
                                        onLoad={handleLogoLoad}
                                        style={logoDimensions
                                            ? { width: logoDimensions.width, height: logoDimensions.height }
                                            : { height: MAX_LOGO_HEIGHT, width: 'auto' }
                                        }
                                    />
                                ) : ''}
                            </Box>
                            <Box>
                                <Typography className={classes.userName}>{userName}</Typography>
                                <Typography className={classes.userRole}>{userRole}</Typography>
                            </Box>
                            <KeyboardArrowDownIcon className={classes.arrowIcon} />
                        </>
                    )}
                    inputProps={{
                        MenuProps: {
                            MenuListProps: {
                                sx: {
                                    backgroundColor: theme.palette.secondary.dark,
                                },
                            },
                        },
                    }}
                >
                    <MenuItem value="/manage-account" className={classes.selectMenuItem}>
                        <ManageAccountsIcon />
                        Manage Account
                    </MenuItem>
                    <MenuItem value="logout" className={`${classes.selectMenuItem} ${classes.selectMenuItemDanger}`}>
                        <LogoutIcon />
                        Sign Out
                    </MenuItem>
                </Select>
            </FormControl>
        </Box>
    );
};

export default Header;
